import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import DataRoom from "@/components/company/DataRoom";
import PitchDeckPreview from "@/components/company/PitchDeckPreview";
import CapTableChart from "@/components/company/CapTableChart";
import {
  MapPin, Globe, LinkedinLogo, CalendarBlank, Bookmark, ChatCircleText,
  Handshake, Star, ArrowUpRight, Buildings, Rocket, Target,
  TrendUp, ChartLineUp, Users, Certificate, Money, Timer,
  Package, Fire, Lightning, Gauge, Clock, Wallet, Lightbulb, ShieldStar,
  Trophy, Path, Question, PuzzlePiece, Storefront, GlobeHemisphereWest, Sparkle,
  CurrencyGbp, ChartBar, HandCoins, PencilSimple, Files,
} from "@phosphor-icons/react";
import FcaDisclaimerBand from "@/components/compliance/FcaDisclaimerBand";
import { formatMoney, formatPct, formatNumber, formatDate } from "@/lib/format";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar, CartesianGrid } from "recharts";
import { toast } from "sonner";

const Badge = ({ children, tone = "default" }) => {
  const tones = {
    default: "bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-200",
    accent: "bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300",
    amber: "bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
    slate: "bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900",
  };
  return <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide ${tones[tone]}`}>{children}</span>;
};

const HighlightCarousel = ({ metrics, fundraising, stage }) => {
  const m = metrics || {};
  const fr = fundraising || {};
  const items = [];
  const arrValue = m.arr || m.annual_revenue;
  if (arrValue) items.push({ icon: CurrencyGbp, label: m.arr ? "ARR" : "Annual Revenue", value: formatMoney(arrValue), tone: "emerald" });
  if (m.growth_rate) items.push({ icon: TrendUp, label: "Growth YoY", value: formatPct(m.growth_rate), tone: "emerald" });
  if (m.gross_margin) items.push({ icon: Gauge, label: "Gross Margin", value: formatPct(m.gross_margin), tone: "blue" });
  if (m.customers) items.push({ icon: Users, label: "Customers", value: formatNumber(m.customers), tone: "violet" });
  else if (m.countries_served && m.countries_served > 1) items.push({ icon: GlobeHemisphereWest, label: "Countries", value: formatNumber(m.countries_served), tone: "violet" });
  if (m.employees) items.push({ icon: Buildings, label: "Team", value: formatNumber(m.employees), tone: "amber" });
  if (m.patents) items.push({ icon: Certificate, label: "Patents", value: formatNumber(m.patents), tone: "amber" });
  if (items.length === 0 && fr.valuation) items.push({ icon: ChartBar, label: "Valuation", value: formatMoney(fr.valuation), tone: "emerald" });
  if (items.length === 0) return null;

  const toneClasses = {
    emerald: "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400",
    blue: "text-blue-700 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-400",
    amber: "text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-400",
    violet: "text-violet-700 bg-violet-100 dark:bg-violet-900/40 dark:text-violet-400",
  };

  return (
    <div className="mb-10">
      <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">At a glance</div>
      <div className="flex flex-wrap gap-3">
        {items.slice(0, 6).map((it, i) => (
          <div key={i} className="flex items-center gap-3 pl-3 pr-5 py-3 rounded-2xl bg-card border border-border shadow-[0_1px_2px_0_rgba(15,23,42,0.04)] hover:border-emerald-400 hover:shadow-md transition-all">
            <div className={`h-10 w-10 rounded-xl grid place-items-center shrink-0 ${toneClasses[it.tone]}`}>
              <it.icon size={18} weight="fill" />
            </div>
            <div>
              <div className="text-[9px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{it.label}</div>
              <div className="num-display font-display font-bold text-xl leading-none mt-1 tracking-tight">{it.value}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const Metric = ({ label, value, suffix, icon: Icon, tone = "default" }) => {
  const tones = {
    default: "text-slate-500 bg-slate-100 dark:bg-slate-800 dark:text-slate-400",
    emerald: "text-emerald-700 bg-emerald-100 dark:bg-emerald-900/40 dark:text-emerald-400",
    blue: "text-blue-700 bg-blue-100 dark:bg-blue-900/40 dark:text-blue-400",
    amber: "text-amber-700 bg-amber-100 dark:bg-amber-900/40 dark:text-amber-400",
    violet: "text-violet-700 bg-violet-100 dark:bg-violet-900/40 dark:text-violet-400",
    rose: "text-rose-700 bg-rose-100 dark:bg-rose-900/40 dark:text-rose-400",
  };
  return (
    <div className="group p-5 rounded-2xl border border-border bg-card hover:border-emerald-500 hover:shadow-md transition-all duration-300 relative overflow-hidden">
      <div className="flex items-start justify-between gap-3">
        <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold">{label}</div>
        {Icon && (
          <div className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${tones[tone]}`}>
            <Icon size={16} weight="fill" />
          </div>
        )}
      </div>
      <div className="mt-2 num-display font-display text-[26px] font-bold tracking-tight leading-none">
        {value}{suffix && <span className="text-sm text-muted-foreground ml-1 font-medium">{suffix}</span>}
      </div>
    </div>
  );
};

const MetricGroup = ({ title, icon: Icon, children }) => (
  <div>
    <div className="flex items-center gap-2 mb-4">
      {Icon && (
        <div className="h-6 w-6 rounded-md bg-emerald-600 grid place-items-center">
          <Icon size={13} weight="fill" className="text-white" />
        </div>
      )}
      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-foreground">{title}</div>
      <div className="flex-1 h-px bg-border" />
    </div>
    <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-3">{children}</div>
  </div>
);

const SECTION_META = {
  "Problem": { icon: Question, color: "rose" },
  "Solution": { icon: Lightbulb, color: "emerald" },
  "Market Opportunity": { icon: GlobeHemisphereWest, color: "blue" },
  "Business Model": { icon: PuzzlePiece, color: "violet" },
  "Competitive Advantage": { icon: ShieldStar, color: "amber" },
  "Target Customers": { icon: Storefront, color: "blue" },
  "Traction": { icon: TrendUp, color: "emerald" },
  "Roadmap": { icon: Path, color: "violet" },
  "Investment Ask": { icon: HandCoins, color: "emerald" },
  "Use of Funds": { icon: Target, color: "emerald" },
  "Investor Rights": { icon: ShieldStar, color: "blue" },
  "Exit Strategy": { icon: Trophy, color: "amber" },
};

const Section = ({ title, children, html }) => {
  const meta = SECTION_META[title] || { icon: Sparkle, color: "emerald" };
  const Icon = meta.icon;
  const colorMap = {
    emerald: "text-emerald-700 dark:text-emerald-400 bg-emerald-100 dark:bg-emerald-900/40",
    rose: "text-rose-700 dark:text-rose-400 bg-rose-100 dark:bg-rose-900/40",
    blue: "text-blue-700 dark:text-blue-400 bg-blue-100 dark:bg-blue-900/40",
    violet: "text-violet-700 dark:text-violet-400 bg-violet-100 dark:bg-violet-900/40",
    amber: "text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-900/40",
  };
  return (
    <div className="group">
      <div className="flex items-center gap-3 mb-3">
        <div className={`h-9 w-9 rounded-xl grid place-items-center shrink-0 ${colorMap[meta.color]}`}>
          <Icon size={17} weight="fill" />
        </div>
        <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">{title}</h3>
      </div>
      <div className="pl-12">
        {html ? (
          html.trim() ? <div className="rich-content" dangerouslySetInnerHTML={{ __html: html }} /> : <span className="text-muted-foreground italic text-sm">Not provided</span>
        ) : (
          <div className="text-[15px] leading-relaxed text-foreground/90">{children || <span className="text-muted-foreground italic">Not provided</span>}</div>
        )}
      </div>
    </div>
  );
};

const HeroStat = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">{label}</div>
    <div className="num-display font-display text-2xl md:text-3xl font-bold tracking-tight mt-1">{value}</div>
  </div>
);

export default function CompanyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, isTeam, isClient } = useAuth();
  const [c, setC] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const [showMeeting, setShowMeeting] = useState(false);

  useEffect(() => {
    setLoading(true);
    api.get(`/companies/${id}`).then((r) => setC(r.data)).catch(() => nav("/")).finally(() => setLoading(false));
  }, [id, nav]);

  const toggleBookmark = async () => {
    try {
      const r = await api.post(`/bookmarks/${id}`);
      setC((prev) => ({ ...prev, is_bookmarked: r.data.bookmarked }));
      toast.success(r.data.bookmarked ? "Bookmarked" : "Removed from bookmarks");
    } catch { toast.error("Failed to update bookmark"); }
  };

  if (loading || !c) return <div className="p-16 text-muted-foreground">Loading…</div>;

  const fr = c.fundraising || {};
  const m = c.metrics || {};
  const hiddenSections = new Set(c.hidden_overview_sections || []);
  const hiddenTabs = new Set(c.hidden_tabs || []);
  const ALL_TABS = [
    ["overview", "Overview"],
    ["metrics", "Key Metrics"],
    ["founders", "Founders"],
    ["team", "Team"],
    ["financials", "Financials"],
    ["fundraising", "Fundraising"],
    ["captable", "Cap Table"],
    ["dataroom", "Data Room"],
  ];
  const visibleTabs = ALL_TABS.filter(([v]) => !hiddenTabs.has(v));
  // Overview sidebar only renders when there's content to show in it.
  const hasSidebar = Boolean(fr.use_of_funds);

  return (
    <div>
      {/* FCA Header Disclaimer — required at the top of every Company Profile / Investment Opportunity page.
          Kept in a matching max-w container so it visually aligns with the hero banner below. */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-2">
        <FcaDisclaimerBand slot="header" tone="header" companyName={c.name} />
      </div>
      {/* Hero: banner + company block on left, pitch deck preview on right */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="h-48 md:h-64 relative overflow-hidden rounded-3xl bg-secondary shadow-sm">
          {c.banner_url && <img src={c.banner_url} alt="" className="h-full w-full object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-b from-black/10 via-black/20 to-black/40" />
        </div>
        <div className="-mt-24 relative z-10">
          <div className="rounded-3xl border border-border bg-card shadow-xl overflow-hidden">
            <div className="p-6 md:p-10">
              {/* Top row: logo + name + actions */}
              <div className="flex flex-col md:flex-row md:items-start gap-6">
                <div className="h-24 w-24 md:h-28 md:w-28 rounded-2xl bg-secondary overflow-hidden shrink-0 border-4 border-card shadow-lg">
                  {c.logo_url ? <img src={c.logo_url} alt="" className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center text-3xl font-display font-bold">{c.name?.[0]}</div>}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-1.5 mb-3">
                    {c.featured && <Badge tone="slate"><Star size={10} weight="fill" className="mr-1" />Featured</Badge>}
                    {c.priority === "High" && <Badge tone="amber">Priority</Badge>}
                    <Badge tone="default">{c.stage}</Badge>
                    <Badge tone="default">{c.revenue_status}</Badge>
                    {c.eis && <Badge tone="accent">EIS</Badge>}
                    {c.seis && <Badge tone="accent">SEIS</Badge>}
                  </div>
                  <h1 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.02]" data-testid="company-name">
                    {c.name}
                  </h1>
                  <p className="mt-3 text-lg md:text-xl text-muted-foreground leading-snug max-w-3xl">{c.tagline}</p>
                  <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5"><Buildings size={14} /> {c.industry}</div>
                    <div className="flex items-center gap-1.5"><MapPin size={14} /> {c.location}, {c.country}</div>
                    {c.founded && <div className="flex items-center gap-1.5"><CalendarBlank size={14} /> Founded {c.founded}</div>}
                    {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-emerald-600"><Globe size={14} /> Website <ArrowUpRight size={11} /></a>}
                    {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="flex items-center gap-1.5 hover:text-emerald-600"><LinkedinLogo size={14} /> LinkedIn</a>}
                  </div>
                </div>
                <div className="flex md:flex-col gap-2 shrink-0">
                  {(() => {
                    const ownsAsClient = isClient && c.client_email && c.client_email.toLowerCase() === (user?.email || "").toLowerCase();
                    const canEdit = isTeam || ownsAsClient;
                    if (canEdit) {
                      return (
                        <>
                          <Button
                            onClick={() => nav(`/admin/companies/${c.id}/edit`)}
                            data-testid="edit-company-btn"
                            className="h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white"
                          >
                            <PencilSimple size={16} className="mr-2" /> Edit company
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => nav(`/admin/companies/${c.id}/documents`)}
                            data-testid="manage-docs-btn"
                            className="h-11"
                          >
                            <Files size={16} className="mr-2" /> Manage documents
                          </Button>
                        </>
                      );
                    }
                    return (
                      <>
                        <Button onClick={() => setShowMeeting(true)} data-testid="request-meeting-btn" className="h-11 bg-slate-900 hover:bg-slate-800 text-white dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white">
                          <Handshake size={16} className="mr-2" /> Request Meeting
                        </Button>
                        <Button variant="outline" onClick={() => setShowEnquiry(true)} data-testid="contact-btn" className="h-11">
                          <ChatCircleText size={16} className="mr-2" /> Contact
                        </Button>
                        <Button variant="outline" onClick={toggleBookmark} data-testid="bookmark-btn" className="h-11">
                          <Bookmark size={16} weight={c.is_bookmarked ? "fill" : "regular"} className={`mr-2 ${c.is_bookmarked ? "text-emerald-600" : ""}`} />
                          {c.is_bookmarked ? "Saved" : "Bookmark"}
                        </Button>
                      </>
                    );
                  })()}
                </div>
              </div>

              {/* Split hero: pitch deck preview + investment terms */}
              <div className="mt-10 grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-8">
                <PitchDeckPreview companyId={c.id} companyName={c.name} />
                <div className="space-y-1">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-bold mb-4">Investment Terms</div>
                  <div className="grid grid-cols-2 gap-6 py-2">
                    <HeroStat label="Raising" value={formatMoney(fr.amount_raising)} />
                    <HeroStat label="Total Raised" value={formatMoney(fr.total_funding_raised)} />
                    <HeroStat label="Grants" value={formatMoney(fr.grants_amount)} />
                    <HeroStat label="Equity / Loans" value={formatMoney(fr.equity_loans_amount)} />
                  </div>
                  {fr.expected_closing_date && (
                    <div className="mt-2 pt-5 border-t border-border">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold">Expected Closing</div>
                      <div className="num-display font-display text-lg font-bold mt-1">{formatDate(fr.expected_closing_date)}</div>
                    </div>
                  )}
                  {c.investment_highlights?.length > 0 && (
                    <div className="mt-5 pt-5 border-t border-border">
                      <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-semibold mb-3 flex items-center gap-1.5">
                        <Rocket size={11} weight="fill" /> Highlights
                      </div>
                      <ul className="space-y-2">
                        {c.investment_highlights.slice(0, 6).map((h, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm">
                            <div className="mt-1.5 h-1 w-1 rounded-full bg-emerald-600 shrink-0" />
                            <span>{h}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8 py-10">
        <Tabs defaultValue="overview" className="w-full">
          <TabsList className="bg-transparent p-0 h-auto border-b border-border rounded-none w-full justify-start gap-8 overflow-x-auto">
            {visibleTabs.map(([v, l]) => (
              <TabsTrigger
                key={v}
                value={v}
                data-testid={`tab-${v}`}
                className="rounded-none border-b-2 border-transparent data-[state=active]:border-emerald-600 data-[state=active]:bg-transparent data-[state=active]:shadow-none px-0 pb-4 h-auto text-[15px] font-semibold text-muted-foreground data-[state=active]:text-foreground"
              >
                {l}
              </TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="overview" className="mt-8 space-y-10">
            <HighlightCarousel metrics={m} fundraising={fr} stage={c.stage} />
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
              <div className={hasSidebar ? "lg:col-span-2 space-y-10" : "lg:col-span-3 space-y-10"}>
                <div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="h-9 w-9 rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 grid place-items-center shrink-0">
                      <Buildings size={17} weight="fill" />
                    </div>
                    <h3 className="font-display text-xl md:text-2xl font-bold tracking-tight">About</h3>
                  </div>
                  <div className="pl-12">
                    {c.description ? (
                      <div className="rich-content text-[17px]" dangerouslySetInnerHTML={{ __html: c.description }} />
                    ) : (
                      <p className="text-[17px] text-foreground/90 leading-relaxed italic text-muted-foreground">No description provided.</p>
                    )}
                  </div>
                </div>
                {!hiddenSections.has("problem") && <Section title="Problem" html={c.problem} />}
                {!hiddenSections.has("solution") && <Section title="Solution" html={c.solution} />}
                {!hiddenSections.has("market_opportunity") && <Section title="Market Opportunity" html={c.market_opportunity} />}
                {!hiddenSections.has("business_model") && <Section title="Business Model" html={c.business_model} />}
                {!hiddenSections.has("competitive_advantage") && <Section title="Competitive Advantage" html={c.competitive_advantage} />}
                {!hiddenSections.has("target_customers") && <Section title="Target Customers" html={c.target_customers} />}
                {!hiddenSections.has("traction") && <Section title="Traction" html={c.traction} />}
                {!hiddenSections.has("roadmap") && <Section title="Roadmap" html={c.roadmap} />}
                {!hiddenSections.has("investment_ask") && <Section title="Investment Ask" html={c.investment_ask} />}
              </div>
              {hasSidebar && (
                <div className="space-y-6">
                  {fr.use_of_funds && (
                    <div className="rounded-2xl border border-border bg-card p-6">
                      <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3 flex items-center gap-1.5"><Target size={12} weight="fill" /> Use of Funds</div>
                      <div className="rich-content text-sm leading-relaxed" dangerouslySetInnerHTML={{ __html: fr.use_of_funds }} />
                    </div>
                  )}
                </div>
              )}
            </div>
          </TabsContent>

          <TabsContent value="metrics" className="mt-8 space-y-10">
            <MetricGroup title="Revenue & Growth" icon={TrendUp}>
              <Metric label="Annual Revenue" value={formatMoney(m.annual_revenue)} icon={CurrencyGbp} tone="emerald" />
              <Metric label="MRR" value={formatMoney(m.mrr)} icon={ChartLineUp} tone="emerald" />
              <Metric label="ARR" value={formatMoney(m.arr)} icon={ChartBar} tone="emerald" />
              <Metric label="Growth Rate" value={formatPct(m.growth_rate)} icon={TrendUp} tone="emerald" />
              <Metric label="Gross Margin" value={formatPct(m.gross_margin)} icon={Gauge} tone="blue" />
              <Metric label="EBITDA" value={formatMoney(m.ebitda)} icon={Money} tone="blue" />
            </MetricGroup>

            <MetricGroup title="Company Scale" icon={Buildings}>
              <Metric label="Customers" value={formatNumber(m.customers)} icon={Users} tone="violet" />
              <Metric label="Employees" value={formatNumber(m.employees)} icon={Users} tone="violet" />
              <Metric label="Countries Served" value={formatNumber(m.countries_served)} icon={GlobeHemisphereWest} tone="violet" />
              <Metric label="Patents" value={formatNumber(m.patents)} icon={Certificate} tone="amber" />
            </MetricGroup>

            <MetricGroup title="Financial Health" icon={Wallet}>
              <Metric label="Runway" value={formatNumber(m.runway_months)} suffix="mo" icon={Clock} tone="amber" />
              <Metric label="Burn Rate" value={formatMoney(m.burn_rate)} suffix="/mo" icon={Fire} tone="rose" />
              <Metric label="Valuation" value={formatMoney(m.valuation)} icon={ChartBar} tone="blue" />
              <Metric label="Previous Funding" value={formatMoney(m.previous_funding)} icon={HandCoins} tone="blue" />
            </MetricGroup>

            {m.ip_status && (
              <MetricGroup title="Intellectual Property" icon={Certificate}>
                <div className="col-span-2 md:col-span-3 xl:col-span-4 p-5 rounded-2xl border border-border bg-card">
                  <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground font-bold mb-1">IP Status</div>
                  <div className="font-display font-bold text-lg">{m.ip_status}</div>
                </div>
              </MetricGroup>
            )}
          </TabsContent>

          <TabsContent value="founders" className="mt-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
              {(c.founders || []).map((f, i) => (
                <div key={i} className="rounded-2xl border border-border bg-card p-6 hover:shadow-md hover:border-emerald-500 transition-all">
                  <div className="flex gap-5">
                    <div className="relative shrink-0">
                      <div className="h-20 w-20 rounded-2xl bg-secondary overflow-hidden ring-2 ring-card shadow-md">
                        {f.photo_url ? <img src={f.photo_url} alt={f.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center font-display font-bold text-2xl bg-gradient-to-br from-emerald-500 to-emerald-700 text-white">{f.name?.[0]}</div>}
                      </div>
                      <div className="absolute -bottom-1 -right-1 h-6 w-6 rounded-full bg-emerald-600 grid place-items-center ring-2 ring-card">
                        <Trophy size={11} weight="fill" className="text-white" />
                      </div>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="font-display font-bold text-lg leading-tight">{f.name}</div>
                      <div className="text-sm text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5">{f.designation}</div>
                      {f.experience && <div className="text-xs text-muted-foreground mt-1.5 flex items-center gap-1"><Sparkle size={11} weight="fill" /> {f.experience}</div>}
                    </div>
                  </div>
                  {f.biography && (
                    <div className="mt-4 pt-4 border-t border-border rich-content text-sm" dangerouslySetInnerHTML={{ __html: f.biography }} />
                  )}
                  {f.linkedin && (
                    <a href={f.linkedin} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 mt-4 text-xs text-emerald-700 dark:text-emerald-400 font-semibold hover:underline">
                      <LinkedinLogo size={13} weight="fill" /> LinkedIn profile
                    </a>
                  )}
                </div>
              ))}
              {(!c.founders || c.founders.length === 0) && <div className="text-muted-foreground italic col-span-2">No founders listed.</div>}
            </div>
          </TabsContent>

          <TabsContent value="team" className="mt-8">
            {["leadership", "employee", "advisor"].map((cat) => {
              const members = (c.team || []).filter((t) => t.category === cat);
              if (members.length === 0) return null;
              const label = cat === "leadership" ? "Leadership" : cat === "advisor" ? "Advisors" : "Key Employees";
              return (
                <div key={cat} className="mb-8">
                  <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">{label}</div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {members.map((t, i) => (
                      <div key={i} className="rounded-2xl border border-border bg-card p-5 text-center">
                        <div className="h-16 w-16 rounded-full bg-secondary overflow-hidden mx-auto">
                          {t.photo_url ? <img src={t.photo_url} alt={t.name} className="h-full w-full object-cover" /> : <div className="h-full w-full grid place-items-center font-bold">{t.name?.[0]}</div>}
                        </div>
                        <div className="mt-3 font-display font-bold">{t.name}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{t.designation}</div>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
            {(!c.team || c.team.length === 0) && <div className="text-muted-foreground italic">No team members listed.</div>}
          </TabsContent>

          <TabsContent value="financials" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">Revenue Trend</div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={m.historical_revenue || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => v >= 1e6 ? `${v / 1e6}M` : v >= 1e3 ? `${v / 1e3}k` : v} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatMoney(v)} />
                      <Line type="monotone" dataKey="revenue" stroke="hsl(161 94% 30%)" strokeWidth={2.5} dot={{ r: 4 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="rounded-2xl border border-border bg-card p-6">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-muted-foreground mb-4">EBITDA Trend</div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={m.historical_revenue || []}>
                      <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
                      <XAxis dataKey="year" stroke="hsl(var(--muted-foreground))" fontSize={11} />
                      <YAxis stroke="hsl(var(--muted-foreground))" fontSize={11} tickFormatter={(v) => v >= 1e6 || v <= -1e6 ? `${(v / 1e6).toFixed(1)}M` : v} />
                      <Tooltip contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 8, fontSize: 12 }} formatter={(v) => formatMoney(v)} />
                      <Bar dataKey="ebitda" fill="hsl(217 91% 60%)" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="fundraising" className="mt-8">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-10">
                <Section title="Use of Funds" html={fr.use_of_funds} />
                <Section title="Investor Rights" html={fr.investor_rights} />
                <Section title="Exit Strategy" html={fr.exit_strategy} />
              </div>
              <div className="lg:sticky lg:top-20 h-fit">
                <div className="rounded-2xl border border-border bg-gradient-to-br from-slate-900 to-slate-800 dark:from-slate-950 dark:to-slate-900 p-6 text-white shadow-xl overflow-hidden relative">
                  <div className="absolute inset-0 grid-backdrop opacity-30 pointer-events-none" />
                  <div className="relative">
                    <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-emerald-400 mb-4">Investment Terms</div>
                    <div>
                      <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">Amount Raising</div>
                      <div className="num-display font-display text-4xl font-bold mt-1 text-emerald-400 tracking-tight">{formatMoney(fr.amount_raising)}</div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 mt-5 pt-5 border-t border-white/10">
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">Total Raised</div>
                        <div className="num-display font-display text-xl font-bold mt-1">{formatMoney(fr.total_funding_raised)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">Grants</div>
                        <div className="num-display font-display text-xl font-bold mt-1">{formatMoney(fr.grants_amount)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">Equity / Loans</div>
                        <div className="num-display font-display text-xl font-bold mt-1">{formatMoney(fr.equity_loans_amount)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-[0.15em] text-white/50 font-semibold">Closes</div>
                        <div className="num-display font-display text-lg font-bold mt-1">{formatDate(fr.expected_closing_date)}</div>
                      </div>
                    </div>
                    <div className="mt-5 pt-5 border-t border-white/10 flex flex-wrap gap-1.5">
                      {c.eis && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40">EIS eligible</span>}
                      {c.seis && <span className="inline-flex items-center px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide bg-emerald-500/20 text-emerald-300 ring-1 ring-emerald-500/40">SEIS eligible</span>}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="captable" className="mt-8">
            <div className="rounded-2xl border border-border bg-card p-6 md:p-8">
              <div className="mb-6">
                <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-emerald-700 dark:text-emerald-400 mb-2">Post-Money Ownership</div>
                <h3 className="font-display text-2xl md:text-3xl font-bold tracking-tight">Capitalisation Table</h3>
                <p className="mt-2 text-sm text-muted-foreground">Projected shareholding after this round closes. Detailed cap table available in the Data Room.</p>
              </div>
              <CapTableChart entries={c.cap_table || []} />
            </div>
          </TabsContent>

          <TabsContent value="dataroom" className="mt-8">
            <DataRoom companyId={c.id} companyName={c.name} />
          </TabsContent>
        </Tabs>

        {/* FCA Footer Disclaimer — required at the bottom of every Company Profile / Investment Opportunity page */}
        <FcaDisclaimerBand slot="footer" companyName={c.name} />
      </div>

      <EnquiryDialog open={showEnquiry} onClose={() => setShowEnquiry(false)} companyId={c.id} companyName={c.name} />
      <MeetingDialog open={showMeeting} onClose={() => setShowMeeting(false)} companyId={c.id} companyName={c.name} />
    </div>
  );
}

function EnquiryDialog({ open, onClose, companyId, companyName }) {
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/enquiries", { company_id: companyId, subject, message });
      toast.success("Enquiry sent to the investment team");
      setSubject(""); setMessage(""); onClose();
    } catch { toast.error("Failed to send enquiry"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="enquiry-dialog">
        <DialogHeader><DialogTitle>Contact {companyName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Input placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="enquiry-subject-input" />
          <Textarea placeholder="Your message…" rows={5} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="enquiry-message-input" />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !subject || !message} data-testid="enquiry-submit-btn">Send</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function MeetingDialog({ open, onClose, companyId, companyName }) {
  const [date, setDate] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/meetings", { company_id: companyId, preferred_date: date || null, message });
      toast.success("Meeting requested");
      setDate(""); setMessage(""); onClose();
    } catch { toast.error("Failed to send meeting request"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="meeting-dialog">
        <DialogHeader><DialogTitle>Request meeting with {companyName}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Preferred date</label>
            <Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="meeting-date-input" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1 block">Message (optional)</label>
            <Textarea placeholder="Any specific areas you'd like to discuss?" rows={4} value={message} onChange={(e) => setMessage(e.target.value)} data-testid="meeting-message-input" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={submit} disabled={busy} data-testid="meeting-submit-btn">Send request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
